import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { ReactComponent as CloseModalIcon } from "../../assets/icons/CloseModalIcon.svg";
import { motion } from "framer-motion";
import { useOutletContext } from "react-router-dom";
import PrimaryButton from "../Generic/PrimaryButton";
import SecondaryButton from "../Generic/SecondaryButton";
import CustomInput from "../Generic/CustomInput";
import { useKeysContext } from "../../context/useKeysContext";
import { useProjectContext } from "../../context/useProjectContext";
import {
  showGenericError,
  showSuccessNotification,
} from "../../helper/Notifications";
import DocumentationPage from "../../Pages/DocumentationPage/DocumentationPage";
import AsyncTranslateDocumentation from "../../Pages/DocumentationPage/AsyncTranslateDocumentation";
import AudioTranscribeDocumentation from "../../Pages/DocumentationPage/AudioTranscribeDocumentation";
import AudioToAudioDocumentation from "../../Pages/DocumentationPage/AudioToAudioDocumentation";
import RealtimeConnectorDocumentation from "../../Pages/DocumentationPage/RealtimeConnectorDocumentation";
import VideoTranslationApiDocumentation from "../../Pages/DocumentationPage/VideoTranslationApiDocumentation";
import MeetingsApiDocumentation from "../../Pages/DocumentationPage/MeetingsApiDocumentation";

const getBaseUrl = () => {
  const env = process.env.REACT_APP_ENV;
  
  if (env === 'test' || env === 'development') {
    return "https://emotii.s3.eu-west-1.amazonaws.com/static_files/testing-env-files";
  }
  
  return "https://emotii.s3.eu-west-1.amazonaws.com/static_files";
};

const BASE_PDF_URL = getBaseUrl();

const ALL_DOCS = {
  translate: {
    label: "Text-to-Text Translation",
    component: DocumentationPage,
    pdfUrl: `${BASE_PDF_URL}/Translation_API_Documentation_v2.pdf`,
    pdfFilename: "Translation_API_Documentation_v2.pdf"
  },
  asyncTranslate: {
    label: "Async Text Translation",
    component: AsyncTranslateDocumentation,
    pdfUrl: `${BASE_PDF_URL}/Async_Text_Translation_Documentation_v1.pdf`,
    pdfFilename: "Async_Text_Translation_Documentation_v1.pdf"
  },
  audioTranscribe: {
    label: "Audio-to-Text Transcription",
    component: AudioTranscribeDocumentation,
    pdfUrl: `${BASE_PDF_URL}/Audio_Transcription_Documentation_v1.pdf`,
    pdfFilename: "Audio_Transcription_Documentation_v1.pdf"
  },
  audioToAudio: {
    label: "Audio-to-Audio Translation",
    component: AudioToAudioDocumentation,
    pdfUrl: `${BASE_PDF_URL}/Audio_to_Audio_Translation_Documentation_v1.pdf`,
    pdfFilename: "Audio_to_Audio_Translation_Documentation_v1.pdf"
  },
  realtimeConnector: {
    label: "API for Voice",
    component: RealtimeConnectorDocumentation,
    pdfUrl: `${BASE_PDF_URL}/Realtime_Connector_documentation_v1.pdf`,
    pdfFilename: "Realtime_Connector_documentation_v1.pdf"
  },
  videoTranslationApi: {
    label: "API for Video",
    component: VideoTranslationApiDocumentation,
    pdfUrl: `${BASE_PDF_URL}/Video_Translation_API_v1_Documentation.pdf`,
    pdfFilename: "Video_Translation_API_v1_Documentation.pdf"
  },
  meetingsApi: {
    label: "API for Meetings",
    component: MeetingsApiDocumentation,
  },
};

const DocumentationPageModal = ({ isMoldaOpen, setShowDocumentationModal, subscriptionDetails, productType = "api"}) => {
  const { createKey, getKeys } = useKeysContext();
  const { selectedProject } = useProjectContext();

  const [inputValue, setInputValue] = useState("");
  const [activeDoc, setActiveDoc] = useState(
    productType === "realtime_api"
      ? "realtimeConnector"
      : productType === "video_translation_api"
      ? "videoTranslationApi"
      : productType === "meetings_api"
      ? "meetingsApi"
      : "translate"
  );
  const ref = useRef();
  const { setLoading } = useOutletContext();


  const hasRealtimeAccess = 
  subscriptionDetails?.items?.some(i => i.product_slug === "realtime_api") || 
  subscriptionDetails?.details?.realtime_api_enabled === true;

  const availableDocs = useMemo(() => {
    if(productType==="realtime_api"){
      return {realtimeConnector: ALL_DOCS.realtimeConnector};
    }

    if(productType==="video_translation_api"){
      return {videoTranslationApi: ALL_DOCS.videoTranslationApi};
    }

    if(productType==="meetings_api"){
      return {meetingsApi: ALL_DOCS.meetingsApi};
    }

    const standardDocs = { ...ALL_DOCS };

    delete standardDocs.realtimeConnector;
    delete standardDocs.videoTranslationApi;
    delete standardDocs.meetingsApi;

    return standardDocs;

  }, [productType]);

  const handleGetKeys = (projectId) => {
    getKeys(
      projectId,
      1, // page
      25, // items per page
      (response) => {
        setLoading(false);
      },
      (error) => {
        showGenericError();
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (
        setShowDocumentationModal &&
        ref.current &&
        !ref.current.contains(e.target)
      ) {
        setShowDocumentationModal(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [setShowDocumentationModal]);

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setShowDocumentationModal(false);
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);

  const handleExport = async () => {
    try {
      setLoading(true);
      const { pdfUrl, pdfFilename } = availableDocs[activeDoc];
      if (!pdfUrl || !pdfFilename) {
        setLoading(false);
        showGenericError();
        return;
      }

      // Fetch the PDF as a blob to avoid CORS issues with direct download
      const response = await fetch(pdfUrl);
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      setLoading(false);
      showSuccessNotification("Documentation exported successfully");
    } catch (error) {
      setLoading(false);
      showGenericError();
      console.error("Error downloading PDF:", error);
    }
  };

  const ActiveComponent = availableDocs[activeDoc].component;
  const canExport = Boolean(availableDocs[activeDoc].pdfUrl);

  return (
    <FullscreenContainer>
      <Line>
        <HeadWrapper>
          <h1>API Documentation</h1>
        </HeadWrapper>
        <ButtonGroup>
          <SecondaryButton
            icon={<DownloadIcon />}
            text="Export"
            onClick={handleExport}
            variant="neutral"
            disabled={!canExport}
          />
          <SecondaryButton
            text="Close"
            onClick={() => setShowDocumentationModal(false)}
            variant="neutral"
          />
        </ButtonGroup>
      </Line>
      <ContentWrapper>
        <Sidebar>
          {Object.entries(availableDocs).map(([key, { label }]) => (
            <SidebarItem
              key={key}
              $active={activeDoc === key}
              onClick={() => setActiveDoc(key)}
            >
              {label}
            </SidebarItem>
          ))}
        </Sidebar>
        <DocContent>
          <ActiveComponent />
        </DocContent>
      </ContentWrapper>
    </FullscreenContainer>
  );
};

export default DocumentationPageModal;

const FullscreenContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 100;
`;

const CloneBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  border: none;
  background: none;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  h1 {
    color: ${lightThemeColors.darkColor};
    font-size: 30px;
    font-weight: 600;
    line-height: 37.5px;
  }
  p {
    color: ${lightThemeColors.darkColorFaded30};
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between; /* Distributes space between items */
  padding: 20px;
  width: 100%; /* Ensures it fills the entire container */
  border-bottom: 1px solid #e5e7eb;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  overflow: hidden;
`;

const Sidebar = styled.nav`
  width: 240px;
  background: ${lightThemeColors.background || "#f9fafb"};
  border-right: 1px solid #e5e7eb;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
`;

const SidebarItem = styled.button`
  text-align: left;
  padding: 10px 14px;
  border-radius: 8px;
  border: none;
  background: ${(props) => (props.$active ? lightThemeColors.primaryColor : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : lightThemeColors.darkColor)};
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(props) => (props.$active ? lightThemeColors.primaryColor : "#e5e7eb")};
  }
`;

const DocContent = styled.div`
  flex: 1;
  overflow: auto;
`;

const DownloadIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
